import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from "reactstrap";
import { FaTimesCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store/rootReducer";
import { configThunk } from "../../../../../redux/action/config/config.thunk";
import {
  EOProduct,
  Inventory,
  WarehouseInventoryItem,
  InputDistributionRequest,
} from "../../../../../models/config.model";
import { configService } from "../../../../../Services/configService";
import { useLoadingMessage } from "../../../../../components/useLoadingMessage";
import { useAlert } from "react-alert";
import Select from "react-select";

interface EopWithReceived extends EOProduct {
  quantityReceived: number;
  remainingQuantity: number;
}

interface Warehouse {
  value: string;
  label: string;
  managerId?: string | null;
}

interface InputDisributionFormProps {
  openModal: boolean;
  toggle: () => void;
  refreshData: () => void;
  supplierId?: number;
  stateId?: number;
  clusters: any[];
  warehouses: Warehouse[];
  preselectWarehouseId?: string;
}

const InputDistribution: React.FC<InputDisributionFormProps> = ({
  openModal,
  toggle,
  clusters = [],
  warehouses,
  refreshData,
  stateId,
  preselectWarehouseId,
}) => {
  const [eoPs, setEoPs] = useState<EopWithReceived[]>([]);
  const [currentEOProduct, setCurrentEOProduct] = useState<EopWithReceived>({
    id: 0,
    name: "",
    quantity: 0,
    maxQuantity: 0,
    quantityReceived: 0,
    remainingQuantity: 0,
  });

  // const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(() => {
    if (preselectWarehouseId && warehouses.length > 0) {
      return warehouses.find((w) => w.value === preselectWarehouseId) || null;
    }
    return null;
  });
  const [selectedCluster, setSelectedCluster] = useState<any>(null);
  const [deliveryImage, setDeliveryImage] = useState<File | null>(null);
  const [warehouseInventories, setWarehouseInevtories] = useState<
    WarehouseInventoryItem[]
  >([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const { isLoading, message, updateLoader } = useLoadingMessage();
  const { suppliers } = useSelector((state: RootState) => state.suppliers);
  const dispatch = useDispatch();
  const alert = useAlert();

  useEffect(() => {
    if (preselectWarehouseId && warehouses.length > 0) {
      const predefinedWarehouse = warehouses.find(
        (w) => w.value === preselectWarehouseId
      );
      if (predefinedWarehouse && !selectedWarehouse) {
        setSelectedWarehouse(predefinedWarehouse);
        fetchWarehouseInventories(predefinedWarehouse.value);
      }
    }
  }, [preselectWarehouseId, warehouses]);
  useEffect(() => {
    dispatch(configThunk.getAllSuppliers(suppliers));
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchWarehouseInventories(selectedWarehouse.value);
    }
  }, [selectedWarehouse]);

  const fetchWarehouseInventories = async (warehouseUniqueId: string) => {
    updateLoader(true, "Fetching Inventories");
    try {
      const response = await configService.getInventoriesByWarehouse(
        warehouseUniqueId
      );
      if (response && response.data && response.data.data) {
        setWarehouseInevtories(response.data.data);
      } else {
        console.error("Unexpected response structure:", response);
        setWarehouseInevtories([]);
      }
    } catch (error) {
      console.error("Error fetching warehouse inventories:", error);
      alert.error("Failed to fetch warehouse inventories");
      setWarehouseInevtories([]);
    } finally {
      updateLoader(false, "");
    }
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setDeliveryImage(e.target.files[0]);
    }
  };

  const addEOProduct = () => {
    if (currentEOProduct.id > 0 && currentEOProduct.quantityReceived > 0) {
      if (currentEOProduct.quantityReceived > currentEOProduct.maxQuantity) {
        setErrorMessages(["Quantity exceeds the maximum allowed"]);
        return;
      }
      if (eoPs.some((product) => product.id === currentEOProduct.id)) {
        setErrorMessages(["Product already added"]);
        return;
      }
      setEoPs((prevEoPs) => [...prevEoPs, currentEOProduct]);
      setCurrentEOProduct({
        id: 0,
        name: "",
        quantity: 0,
        maxQuantity: 0,
        quantityReceived: 0,
        remainingQuantity: 0,
      });
      setErrorMessages([]);
    } else {
      setErrorMessages([
        "Please select a valid product and enter a valid quantity",
      ]);
    }
  };

  const removeEOProduct = (index: number) => {
    setEoPs((prevEoPs) => prevEoPs.filter((_, i) => i !== index));
  };

  const handleWarehouseChange = (selectedOption: any) => {
    if (!preselectWarehouseId) {
      setSelectedWarehouse(selectedOption);
      setCurrentEOProduct({
        id: 0,
        name: "",
        quantity: 0,
        maxQuantity: 0,
        quantityReceived: 0,
        remainingQuantity: 0,
      });
      setEoPs([]);
      // setWarehouseInevtories([]);
    }
  };

  const handleProductChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedEopId = parseInt(e.target.value);
    const selectedInventory = warehouseInventories.find(
      (item) => item.eop.eopId === selectedEopId
    );

    if (selectedInventory) {
      setCurrentEOProduct({
        id: selectedInventory.eop.eopId,
        name: selectedInventory.eop.eopName,
        quantity: selectedInventory.totalQuantity,
        maxQuantity: selectedInventory.totalQuantity,
        quantityReceived: 0,
        remainingQuantity: selectedInventory.totalQuantity,
      });
    }
  };

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputQuantity = parseInt(e.target.value) || 0;
    const maxQuantityAvailable = currentEOProduct.maxQuantity;

    if (inputQuantity <= maxQuantityAvailable) {
      setCurrentEOProduct((prev) => ({
        ...prev,
        quantityReceived: inputQuantity,
        remainingQuantity: maxQuantityAvailable - inputQuantity,
      }));
      setErrorMessages([]);
    } else {
      setErrorMessages([
        `Quantity cannot exceed available quantity of ${maxQuantityAvailable}`,
      ]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessages([]);

    if (eoPs.length === 0) {
      setErrorMessages(["Please add at least one product before submitting."]);
      return;
    }

    if (!selectedWarehouse || !selectedCluster) {
      setErrorMessages(["Please select both a warehouse and a cluster."]);
      return;
    }

    const inputDistributionRequestBody: InputDistributionRequest = {
      warehouseUniqueId: selectedWarehouse.value,
      ClusterUniqueId: selectedCluster.value,
      managerId: selectedWarehouse.managerId || null,
      stateId: stateId ?? 0,
      imageFile: deliveryImage || undefined,
      eopRequests: eoPs
        .filter((eop) => eop.quantityReceived > 0)
        .map(({ id, quantityReceived }) => ({
          eopId: Number(id),
          quantity: Number(quantityReceived),
        })),
    };

    try {
      const response = await configService.inputDistribution(
        inputDistributionRequestBody
      );
      alert.success(response.data.message);
      refreshData();
      setSelectedCluster(null);
      setSelectedWarehouse(null);
      setDeliveryImage(null);
      setWarehouseInevtories([]);
      setCurrentEOProduct({
        id: 0,
        name: "",
        quantity: 0,
        maxQuantity: 0,
        quantityReceived: 0,
        remainingQuantity: 0,
      });
      setEoPs([]);
      toggle();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const errors = Object.entries(error.response.data.errors)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return messages.map((msg) => `${field}: ${msg}`);
            }
            return `${field}: ${messages}`;
          })
          .flat();
        setErrorMessages(errors);
      } else {
        setErrorMessages([
          error.response?.data?.message || "An error occurred",
        ]);
      }
    }
  };

  return (
    <Modal isOpen={openModal} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Input Distribution</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label for="warehouse">Warehouse</Label>
                <Select
                  options={warehouses}
                  isSearchable
                  placeholder="Select a Warehouse"
                  value={selectedWarehouse}
                  onChange={handleWarehouseChange}
                  isDisabled={!!preselectWarehouseId}
                  className={
                    preselectWarehouseId ? "preselected-warehouse" : ""
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="cluster">Cluster</Label>
                <Select
                  options={clusters}
                  // isClearable
                  isSearchable
                  placeholder="Select a Cluster"
                  value={selectedCluster}
                  onChange={setSelectedCluster}
                />
              </FormGroup>
            </Col>
          </Row>

          {eoPs.map((eoProduct, index) => (
            <Row key={index} className="mb-2 align-items-center">
              <Col md={5}>
                <Input type="text" value={eoProduct.name} readOnly />
              </Col>
              <Col md={5}>
                <Input
                  type="number"
                  value={eoProduct.quantityReceived}
                  readOnly
                />
                <span>Remaining Quantity: {eoProduct.remainingQuantity}</span>
              </Col>
              <Col md={2} className="text-center">
                <Button
                  color="link"
                  onClick={() => removeEOProduct(index)}
                  className="p-0"
                >
                  <FaTimesCircle size={24} color="black" />
                </Button>
              </Col>
            </Row>
          ))}

          <Row className="mb-3">
            <Col md={5}>
              <Input
                type="select"
                value={currentEOProduct.id || ""}
                onChange={handleProductChange}
              >
                <option value="">Select Product</option>
                {warehouseInventories.map((item) => (
                  <option key={item.eop.eopId} value={item.eop.eopId}>
                    {item.eop.eopName} (Available: {item.totalQuantity})
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={5}>
              <Input
                type="number"
                value={currentEOProduct.quantityReceived || ""}
                onChange={handleQuantityChange}
                max={currentEOProduct.maxQuantity}
                min="0"
              />
              <span>
                Remaining Quantity: {currentEOProduct.remainingQuantity}
              </span>
            </Col>
            <Col md={2}>
              <Button color="success" onClick={addEOProduct} block>
                Add
              </Button>
            </Col>
          </Row>

          <FormGroup className="mt-4">
            <Label for="deliveryImage">Upload Delivery Image</Label>
            <Input
              type="file"
              id="imageFile"
              name="deliveryImage"
              accept="image/*"
              onChange={handleImageUpload}
              style={{
                width: "100%",
                padding: "0.375rem 0.75rem",
                border: "1px solid #ced4da",
                borderRadius: "0.25rem",
                lineHeight: "1.5",
                backgroundColor: "#fff",
              }}
            />
          </FormGroup>

          <Button color="success" type="submit" block className="mt-3 mb-2">
            Save
          </Button>
          {errorMessages.length > 0 && (
            <div className="alert alert-danger mb-3">
              <button
                type="button"
                className="close"
                onClick={() => setErrorMessages([])}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
              <ul className="mb-0 pl-3">
                {errorMessages.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default InputDistribution;
